/**
 * Google Maps JS API singleton loader.
 *
 * Multiple components (AddressAutocomplete, TrackingPage) need the same
 * Maps + Places script. If they each append their own <script> tag, we end
 * up with two parallel loads — Maps emits "loaded multiple times", redefines
 * its web components, and crashes with a TypeError on `undefined.kK`. That
 * blocks Places autocomplete and DirectionsService.
 *
 * Call `loadGoogleMaps()` from anywhere — it returns the same Promise on
 * every call and only ever appends one <script> tag.
 */
const SCRIPT_URL_BASE = 'https://maps.googleapis.com/maps/api/js';

let loadPromise = null;

export function loadGoogleMaps({ apiKey, language = 'fr', libraries = ['places'] } = {}) {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('loadGoogleMaps called outside browser'));
  }

  if (window.google?.maps) {
    return Promise.resolve(window.google);
  }

  if (loadPromise) {
    return loadPromise;
  }

  if (!apiKey) {
    return Promise.reject(new Error('loadGoogleMaps: missing apiKey'));
  }

  loadPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-google-maps-loader="1"]');
    if (existing) {
      existing.addEventListener('load', () => resolve(window.google));
      existing.addEventListener('error', () => reject(new Error('Google Maps script failed to load')));
      return;
    }

    // NOTE: do NOT add `loading=async`. With that flag Maps switches to the
    // dynamic-library API — `google.maps.places.Autocomplete` is no longer
    // populated by the time `script.onload` fires; you'd have to call
    // `await google.maps.importLibrary('places')` explicitly. We keep the
    // legacy synchronous load so consumers can read `window.google.maps.places`
    // immediately after the load promise resolves.
    const params = new URLSearchParams({
      key: apiKey,
      libraries: libraries.join(','),
      language,
    });

    const script = document.createElement('script');
    script.src = `${SCRIPT_URL_BASE}?${params.toString()}`;
    script.async = true;
    script.defer = true;
    script.dataset.googleMapsLoader = '1';
    script.onload = () => resolve(window.google);
    script.onerror = () => {
      loadPromise = null;
      reject(new Error('Google Maps script failed to load'));
    };
    document.head.appendChild(script);
  });

  return loadPromise;
}
